import { useEffect, useRef, useState, type RefObject } from 'react';
import { Bold, Italic, Strikethrough, Smile, Eraser } from 'lucide-react';
import { toggleStyle, toPlain, type TextStyle } from '@/lib/textStyle';
import { cn } from '@/lib/utils';

const EMOJI_GROUPS: Array<{ label: string; emojis: string[] }> = [
  {
    label: 'Reactions',
    emojis: ['😀', '😍', '🤩', '😎', '🥳', '🙌', '👏', '👍', '🙏', '💪', '🤝', '👀'],
  },
  {
    label: 'Emphasis',
    emojis: ['🔥', '✨', '⭐', '💥', '🚀', '💡', '⚡', '🎯', '✅', '❗', '❓', '💯'],
  },
  {
    label: 'Business',
    emojis: ['📈', '📊', '💼', '🏆', '🎉', '📣', '📢', '🗓️', '⏰', '💰', '🎁', '🔗'],
  },
  {
    label: 'Places & things',
    emojis: ['📍', '🏠', '🏢', '🚗', '☕', '🍽️', '🛍️', '📷', '🎥', '🎧', '❤️', '💙'],
  },
];

const STYLE_BUTTONS: Array<{ style: TextStyle; icon: typeof Bold; label: string }> = [
  { style: 'bold', icon: Bold, label: 'Bold' },
  { style: 'italic', icon: Italic, label: 'Italic' },
  { style: 'strike', icon: Strikethrough, label: 'Strikethrough' },
];

interface CaptionToolbarProps {
  value: string;
  onChange: (next: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement>;
}

export function CaptionToolbar({ value, onChange, textareaRef }: CaptionToolbarProps) {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [hint, setHint] = useState(false);
  const emojiRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!emojiOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!emojiRef.current?.contains(e.target as Node)) setEmojiOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [emojiOpen]);

  // Nudge the user when they hit a style button with nothing selected.
  useEffect(() => {
    if (!hint) return;
    const t = setTimeout(() => setHint(false), 2500);
    return () => clearTimeout(t);
  }, [hint]);

  const editSelection = (transform: (selected: string) => string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start === end) {
      setHint(true);
      el.focus();
      return;
    }
    const styled = transform(value.slice(start, end));
    onChange(value.slice(0, start) + styled + value.slice(end));
    // The styled run is a different length, so re-select it by its new end.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, start + styled.length);
    });
  };

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    const at = el ? el.selectionEnd : value.length;
    onChange(value.slice(0, at) + emoji + value.slice(at));
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + emoji.length, at + emoji.length);
    });
  };

  const btn =
    'flex items-center justify-center h-7 w-7 rounded-s bg-tertiary text-foreground ring-1 ring-border hover:bg-accent/10 hover:text-accent transition-colors';

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {STYLE_BUTTONS.map(({ style, icon: Icon, label }) => (
        <button
          key={style}
          type="button"
          onClick={() => editSelection((t) => toggleStyle(t, style))}
          title={`${label} — select text first (click again to remove)`}
          className={btn}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
      <button
        type="button"
        onClick={() => editSelection(toPlain)}
        title="Clear styling from the selected text"
        className={btn}
      >
        <Eraser className="h-3.5 w-3.5" />
      </button>

      <div className="relative" ref={emojiRef}>
        <button
          type="button"
          onClick={() => setEmojiOpen((o) => !o)}
          title="Insert emoji"
          className={cn(btn, emojiOpen && 'bg-accent/10 text-accent ring-accent/30')}
        >
          <Smile className="h-3.5 w-3.5" />
        </button>
        {emojiOpen && (
          <div className="absolute z-20 top-9 left-0 w-64 max-h-64 overflow-y-auto bg-popover ring-1 ring-border rounded-m shadow-kumo-l p-2 space-y-2">
            {EMOJI_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1 mb-1">
                  {group.label}
                </p>
                <div className="grid grid-cols-6 gap-0.5">
                  {group.emojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => insertEmoji(emoji)}
                      className="h-8 w-8 text-lg leading-none rounded-s hover:bg-accent/10 transition-colors"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {hint && (
        <span className="text-[11px] text-muted-foreground">Select some text first</span>
      )}
    </div>
  );
}
